import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiEye, FiHeart, FiShare2, FiCalendar } = FiIcons;

const VehicleCard = ({ vehicle, onViewDetails, onScheduleTestDrive, onToggleFavorite, onShare }) => {
  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(vehicle);
    } else {
      // Default action - show vehicle details in a modal or new view
      alert(`Viewing details for ${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}\n\nPrice: $${vehicle.price.toLocaleString()}\nMileage: ${vehicle.mileage.toLocaleString()} mi\nEngine: ${vehicle.engine}\nTransmission: ${vehicle.transmission}\n\nWould you like to schedule a test drive or get more information?`);
    }
  };

  const handleTestDrive = () => {
    if (onScheduleTestDrive) {
      onScheduleTestDrive(vehicle);
    } else {
      // Default action - open test drive scheduler
      const customerName = prompt('Enter your name to schedule a test drive:');
      const customerPhone = prompt('Enter your phone number:');
      
      if (customerName && customerPhone) {
        alert(`Test drive scheduled!\n\nVehicle: ${vehicle.year} ${vehicle.make} ${vehicle.model}\nCustomer: ${customerName}\nPhone: ${customerPhone}\n\nOur team will contact you within 15 minutes to confirm your appointment time.`);
      }
    }
  };

  const handleFavorite = () => {
    if (onToggleFavorite) {
      onToggleFavorite(vehicle);
    } else {
      // Default action
      alert(`${vehicle.year} ${vehicle.make} ${vehicle.model} has been added to your favorites! Sign up for an account to save your favorites permanently.`);
    }
  };

  const handleShare = () => {
    if (onShare) {
      onShare(vehicle);
    } else {
      // Default action - copy vehicle info to clipboard
      const shareText = `Check out this ${vehicle.year} ${vehicle.make} ${vehicle.model} for $${vehicle.price.toLocaleString()}! Only ${vehicle.mileage.toLocaleString()} miles.`;
      
      if (navigator.share) {
        navigator.share({
          title: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
          text: shareText,
          url: window.location.href
        });
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(shareText);
        alert('Vehicle details copied to clipboard!');
      } else {
        alert(`Share this vehicle:\n\n${shareText}`);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 hover:border-audi-red transition-all duration-300 shadow-lg hover:shadow-xl"
    >
      <div className="relative">
        <img
          src={vehicle.image}
          alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          className="w-full h-48 object-cover"
        />
        <div className="absolute top-4 right-4 flex space-x-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleFavorite}
            className="p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 backdrop-blur-sm transition-all"
          >
            <SafeIcon icon={FiHeart} className="w-4 h-4" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleShare}
            className="p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 backdrop-blur-sm transition-all"
          >
            <SafeIcon icon={FiShare2} className="w-4 h-4" />
          </motion.button>
        </div>
        {vehicle.special && (
          <div className="absolute top-4 left-4 bg-audi-red text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
            {vehicle.special}
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="font-bold text-lg text-white mb-1">
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h3>
        <p className="text-gray-400 text-sm mb-3">{vehicle.trim}</p>

        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-2xl font-bold text-audi-red">${vehicle.price.toLocaleString()}</p>
            {vehicle.msrp && vehicle.msrp > vehicle.price && (
              <p className="text-sm text-gray-500 line-through">${vehicle.msrp.toLocaleString()}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-400">{vehicle.mileage.toLocaleString()} mi</p>
            <p className="text-sm text-gray-400">{vehicle.mpg} MPG</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <span className="text-gray-400">Engine:</span>
            <p className="text-white text-xs">{vehicle.engine}</p>
          </div>
          <div>
            <span className="text-gray-400">Transmission:</span>
            <p className="text-white text-xs">{vehicle.transmission}</p>
          </div>
        </div>

        <div className="space-y-2">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleViewDetails}
            className="w-full bg-audi-red hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors font-semibold flex items-center justify-center"
          >
            <SafeIcon icon={FiEye} className="w-4 h-4 mr-2" />
            View Details
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleTestDrive}
            className="w-full bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors font-semibold flex items-center justify-center"
          >
            <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
            Schedule Test Drive
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default VehicleCard;